import './Dashboard.css'

import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { MainLayout, MainPageLayout } from '@/components/layout/MainLayout'
import { Machine } from '@/services/model/machine'
import { useModal } from '@/components/modal/Modal'
import { MachinePath } from '../Common'
import { BackButton } from '@/components/layout/NavBar'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { ActivityStatusChart } from '@/pages/communal/statistics/components/statistic/ActivityStatusChart'
import { Roles } from '@/services/repository/users'
import { formatDate } from '@/common/time'
import { MachineName } from '@/components/machine'


export interface DeviceSessionWithStatisticRow {
  id: string
  imei: string
  ucid: string

  startAt: string
  userId: string | null
  machineId: string | null
  endAt: string | null
  createdAt: string

  activeTime: number
  onFireTime: number
  totalHits: number

  maxHitX: number
  maxHitY: number
  maxHitZ: number

  avgHitX: number
  avgHitY: number
  avgHitZ: number

  maxSpeed: number
  maxCurrent: number | null

  minEngineHours: number | null
  maxEngineHours: number | null
  minUbat: number | null

  // statistic: unknown; // json.RawMessage (use a specific shape if known)
}


interface LocalDeviceSessionWithStatisticRow extends DeviceSessionWithStatisticRow {
  username: string | null
}


export const CommunalMachineStatisticDashboard = () => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const { services, session, translates } = useAppContext()
  const machineId = useParams().mid
  const [machine, setMachine] = useState<Machine>()
  const [deviceSession, setDeviceSession] = useState<LocalDeviceSessionWithStatisticRow>()
  const [generalError, setGeneralError] = useState(false)
  const [now, setNow] = useState<number>(() => Date.now())
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!deviceSession || deviceSession.endAt) return

    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [deviceSession?.endAt, deviceSession?.id])

  const refreshData = async (machineId: string) => {
    try {
      setGeneralError(false)

      // cancel any previous in-flight refresh
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      const machinePromise = machine
        ? Promise.resolve(machine)
        : services.session
          .fetchWithToken(`/api/communal/machines/byid/${machineId}`, {
            signal: controller.signal,
          })
          .then((res) => {
            if (!res.ok) throw new Error(`Machine fetch failed: ${res.status}`);
            return res.json();
          });

      const deviceSessionPromise = services.session
        .fetchWithToken(`/api/communal/sessions/bymachine/${machineId}/current/stat`, {
          signal: controller.signal,
        })
        .then((res) => {
          if (!res.ok) throw new Error(`Session fetch failed: ${res.status}`);
          return res.json();
        });

      const [newMachine, deviceSession] = await Promise.all([
        machinePromise,
        deviceSessionPromise,
      ]);

      setMachine(newMachine)
      setDeviceSession(deviceSession)
    } catch (err: any) {
      // Ignore cancellations
      if (err?.name === "AbortError") return
      setGeneralError(true)
    }
  }

  useEffect(() => {
    if (!machineId) return

    void refreshData(machineId)

    const intervalId = window.setInterval(() => {
      void refreshData(machineId)
    }, 60_000)

    return () => {
      window.clearInterval(intervalId)
      abortRef.current?.abort()
    }
  }, [machineId])

  const calcSessionTimeMs = (
    session: { startAt: string; endAt: string | null },
    nowMs: number
  ): number => {
    const start = new Date(session.startAt).getTime()
    const end = session.endAt ? new Date(session.endAt).getTime() : nowMs

    if (Number.isNaN(start) || Number.isNaN(end)) return 0
    return Math.max(0, end - start)
  }

  const sessionTimeSeconds = useMemo(() => {
    if (!deviceSession) {
      return 0
    }
    return Math.floor(calcSessionTimeMs({ startAt: deviceSession.startAt, endAt: deviceSession.endAt }, now) / 1000)
  }, [deviceSession?.startAt, deviceSession?.endAt, now])

  if (!machine || !translates || !session) {
    return <Spinner />
  }

  if (generalError || !deviceSession) {
    return (
      <MainPageLayout
        pageName='communal-machine-session-dashboard'
        services={services}
        translates={translates}
        session={session}
        path={[
          ...MachinePath({ session, translates, machine }),
          { title: translates.for(`page.machine_session_dashboard.title`) },
        ]}
        navbarLeft={<BackButton services={services} translates={translates} fallbackUrl={`/communal/machine/${machineId}`} />}
      >
        <div className='critic-error-box'>{translates?.for('errors.general_error')}</div>
      </MainPageLayout>
    )
  }

  const t = (key: string) => translates.for(`page.machine_session_dashboard.${key}`)
  const fmt = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v))
  const fmtDate = (iso: string | null) => (iso ? formatDate(new Date(iso)) : '—')
  const fmtSeconds = (sec: number | null | undefined) => {
    if (sec === null || sec === undefined) return '—'
    const s = Math.max(0, Math.floor(sec))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const r = s % 60
    return h > 0 ? `${h}h ${m}m ${r}s` : `${m}m ${r}s`
  }

  const StatRow = ({ label, value, onClick }: { label: string; value: React.ReactNode, onClick?: () => void }) => (
    <div className='cmd-stat-row' onClick={onClick}>
      <div className='cmd-stat-label'>{label}</div>
      <div className='cmd-stat-value'>{value}</div>
    </div>
  )

  return (
    <MainPageLayout
      pageName='communal-machine-session-dashboard'
      services={services}
      translates={translates}
      session={session}
      path={[
        ...MachinePath({ session, translates, machine }),
        { title: translates.for(`page.machine_session_dashboard.title`) },
      ]}
      navbarLeft={<BackButton services={services} translates={translates} fallbackUrl={`/communal/machine/${machineId}`} />}
    >
      <div className='cmd-dashboard'>
        <h2 className='cmd-h2'>{t('current_session')}</h2>

        <div className='cmd-chart-top'>
          <ActivityStatusChart
            activityStatus={{
              activeTime: deviceSession.activeTime,
              onFireTime: deviceSession.onFireTime,
            }}
            chartTitle={t('activity_chart_title')}
            translationsKey='page.machine_session_dashboard.activity_status'
            translates={translates}
          />
        </div>

        {/* Simple operator data*/}
        {[Roles.SuperAdmin, Roles.HyperAdmin, Roles.CompanyAdmin, Roles.Coordinator, Roles.Operator, Roles.Serviceman].includes(session.user.role as Roles) ? <>
          <div className='cmd-card'>
            <h3 className='cmd-h3'>{t('general')}</h3>
            <StatRow label={t('session_time')} value={fmtSeconds(sessionTimeSeconds)} />
          </div>

          <div className='cmd-spacer' />

          <div className='cmd-card'>
            <h3 className='cmd-h3'>{t('statistics')}</h3>
            <StatRow label={t('total_hits')} value={fmt(deviceSession.totalHits)} />

            <StatRow
              label={t('max_hit_xyz')}
              value={`${fmt(Math.max(deviceSession.maxHitX, deviceSession.maxHitY, deviceSession.maxHitZ))}`}
            />
            <StatRow
              label={t('avg_hit_xyz')}
              value={`${fmt(Math.max(deviceSession.avgHitX, deviceSession.avgHitY, deviceSession.avgHitZ))}`}
            />
          </div>
        </> : null}

        {/* Administrator / Manager */}
        {[Roles.SuperAdmin, Roles.HyperAdmin, Roles.CompanyAdmin, Roles.Coordinator, Roles.Operator, Roles.Serviceman].includes(session.user.role as Roles) ? <>
          <div className='cmd-card'>
            <h3 className='cmd-h3'>{t('manager_data_title')}</h3>
            {/* <StatRow label={t('session_id')} value={fmt(deviceSession.id)} />
            <StatRow label={t('imei')} value={fmt(deviceSession.imei)} /> */}
            <StatRow label={t('ucid')} value={fmt(deviceSession.ucid)} onClick={() => {
              if (!deviceSession.userId) {
                return
              }
              navigate(`/communal/statistics/users?&user=${deviceSession.userId}`)
            }} />
            <StatRow label={t('machine')} value={machine.machineType && MachineName({
              row: {
                internalNumber: machine.internalNumber,
                type: machine.machineType
              }
            })} onClick={() => {
              if (!deviceSession.machineId) {
                return
              }
              navigate(`/statistics/machines?machine=${deviceSession.machineId}`)
            }} />
            <StatRow label={t('user')} value={fmt(deviceSession.username)} onClick={() => {
              if (!deviceSession.userId) {
                return
              }
              navigate(`/communal/statistics/users?&user=${deviceSession.userId}`)
            }} />
            <StatRow label={t('start_at')} value={fmtDate(deviceSession.startAt)} />
            <StatRow label={t('end_at')} value={fmtDate(deviceSession.endAt)} />
            <StatRow label={t('session_time')} value={fmtSeconds(sessionTimeSeconds)} />
          </div>

          <div className='cmd-spacer' />

          <div className='cmd-card'>
            <h3 className='cmd-h3'>{t('statistics')}</h3>

            {/* Time */}
            <StatRow label={t('active_time')} value={fmtSeconds(deviceSession.activeTime)} />
            <StatRow label={t('on_fire_time')} value={fmtSeconds(deviceSession.onFireTime)} />

            {/* Hits */}
            <StatRow label={t('total_hits')} value={fmt(deviceSession.totalHits)} />
            <StatRow
              label={t('max_hit_xyz')}
              value={`${fmt(Math.max(deviceSession.maxHitX, deviceSession.maxHitY, deviceSession.maxHitZ))}`}
            />
            <StatRow
              label={t('avg_hit_xyz')}
              value={`${fmt(Math.max(deviceSession.avgHitX, deviceSession.avgHitY, deviceSession.avgHitZ))}`}
            />

            {/* Speed */}
            {/* <StatRow label={t('max_speed')} value={fmt(deviceSession.maxSpeed)} /> */}

            {/* Battery */}
            {/* <StatRow label={t('max_current')} value={fmt(deviceSession.maxCurrent)} />
            <StatRow
              label={t('engine_hours_min_max')}
              value={`${fmt(deviceSession.minEngineHours)} / ${fmt(deviceSession.maxEngineHours)}`}
            />
            <StatRow label={t('min_ubat')} value={fmt(deviceSession.minUbat)} /> */}
          </div>
        </> : null}
      </div>
    </MainPageLayout>
  )
}
